import type {
  CountryCode,
  CreateOrderRequest,
  CreateOrderResponse,
  Language,
  QliroCreateOrderRequest
} from 'ecosystem'
import { parseStoreToBackendOrderLines, paymentShippingFactory, qliroApi } from 'api'
import {
  convertMinorUnitToValue,
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  getFullLocale,
  IS_PRODUCTION,
  resolveUrlPath
} from 'shared-utils'
import type { NextApiRequest, NextApiResponse } from 'next'
import storefrontApi from 'api/storefront-api'
import { qliroOrderLineFactory } from 'api/utils/qliro/checkout'
import { QLIRO_API_KEY } from 'api/utils/qliro/constants'
import { type CreateOrderHandlerOpts } from './types'
import { ingridShippingProvider } from './ingridShippingProvider'
import { nshiftShippingProvider } from './nshiftShippingProvider'

const getQliroLanguage = (locale: Language) => {
  return getFullLocale(locale, {
    no: 'nb-NO',
    en: 'en-US'
  }).toLowerCase()
}

const resolveQliroCountry = (
  opts: CreateOrderHandlerOpts,
  requestedCountryCode: CountryCode | undefined
): CountryCode | undefined => {
  const qliroOptions = opts.paymentProviderConfig?.providerOptions?.qliro
  const { availableCountries, countryCode: configCountryCode } = qliroOptions ?? {}

  if (
    requestedCountryCode &&
    (!availableCountries?.length || availableCountries.includes(requestedCountryCode))
  ) {
    return requestedCountryCode
  }

  return configCountryCode
}

const generateCreateQliroOrderPayload = ({
  opts,
  locale,
  cartDiscountCode,
  cartStateUuid,
  items,
  backendOrder,
  countryCode
}: Pick<CreateOrderRequest, 'cartDiscountCode' | 'items' | 'cartStateUuid'> & {
  locale: Language
  opts: CreateOrderHandlerOpts
  backendOrder: { orderNumber?: string | null }
  countryCode: CountryCode | undefined
}) => {
  const qliroOptions = opts.paymentProviderConfig?.providerOptions?.qliro
  // VAT stays tied to the merchant's configured default country regardless of the shopper's pick.
  const { orderLines } = qliroOrderLineFactory(
    items,
    cartDiscountCode,
    qliroOptions?.countryCode,
    opts.buyerVat
  )
  // Qliro push URLs must be HTTPS on port 443, use a mock domain in dev
  const pushDomain = IS_PRODUCTION ? opts.redirectDomain : 'https://granditude.com'

  return {
    MerchantApiKey: QLIRO_API_KEY,
    MerchantReference: (backendOrder.orderNumber ?? '').substring(0, 25),
    Currency: qliroOptions?.purchaseCurrency || DEFAULT_CURRENCY,
    Country: countryCode || DEFAULT_COUNTRY,
    Language: getQliroLanguage(locale),
    OrderItems: orderLines,
    MerchantConfirmationUrl: resolveUrlPath(
      opts.redirectDomain,
      `/confirmation?order_id=${backendOrder.orderNumber}`
    ),
    MerchantCheckoutStatusPushUrl: resolveUrlPath(
      pushDomain,
      `/api/orders/push?order_id=${backendOrder.orderNumber}`
    ),
    MerchantOrderManagementStatusPushUrl: resolveUrlPath(
      pushDomain,
      `/api/orders/push?order_id=${backendOrder.orderNumber}`
    ),
    MerchantOrderValidationUrl: resolveUrlPath(
      pushDomain,
      `/api/orders/validate-order?order_id=${backendOrder.orderNumber}`
    ),
    MerchantTermsUrl: resolveUrlPath(opts.redirectDomain, qliroOptions?.termsUrl ?? '/terms'),

    ...(qliroOptions?.integrityPolicyUrl
      ? {
          MerchantIntegrityPolicyUrl: resolveUrlPath(
            opts.redirectDomain,
            qliroOptions?.integrityPolicyUrl
          )
        }
      : {}),

    MerchantProvidedMetadata: [
      {
        Key: 'cartStateUuid',
        Value: cartStateUuid ?? ''
      }
    ],
    //styling
    PrimaryColor: qliroOptions?.primaryColor ?? opts.color,
    CallToActionColor: qliroOptions?.callToActionColor ?? opts.color,
    CallToActionHoverColor: qliroOptions?.callToActionHoverColor,
    BackgroundColor: qliroOptions?.backgroundColor,
    CornerRadius: qliroOptions?.cornerRadius,
    ButtonCornerRadius: qliroOptions?.buttonCornerRadius
  } satisfies QliroCreateOrderRequest
}

const simpleShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const {
    storeId,
    items,
    cartDiscountCode,
    cartStateUuid,
    countryCode: requestedCountryCode
  }: CreateOrderRequest = JSON.parse(req.body)
  const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
  const locale = opts.locale || localeForCredentials
  const countryCode = resolveQliroCountry(opts, requestedCountryCode)
  // VAT stays tied to the merchant's configured default country regardless of the shopper's pick.
  const vatCountryCode = opts.paymentProviderConfig?.providerOptions?.qliro?.countryCode

  const { orderAmount, netOrderAmount } = qliroOrderLineFactory(
    items,
    cartDiscountCode,
    vatCountryCode,
    opts.buyerVat
  )
  const shippingInfo = paymentShippingFactory({
    orderAmount,
    netOrderAmount,
    locale,
    opts: opts.shippingProviderConfig?.simpleShipping,
    shippingConfig: opts.shippingProviderConfig,
    customerType: opts.customerType,
    // The shopper's country pick drives the rules; VAT stays on the
    // merchant's configured country.
    recipientCountryCode: countryCode,
    countryCode: vatCountryCode,
    buyerVat: opts.buyerVat
  })

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'QLIRO'
    }
  })

  const qliroPayload = generateCreateQliroOrderPayload({
    opts,
    backendOrder,
    items,
    cartDiscountCode,
    cartStateUuid,
    locale,
    countryCode
  })

  const shippingPriceIncVat = convertMinorUnitToValue(shippingInfo.price)
  const shippingPriceExVat = convertMinorUnitToValue(shippingInfo.price - shippingInfo.tax_amount)

  // Qliro adds the selected `AvailableShippingMethods` entry on top of
  // `OrderItems`. Fine for the legacy single flat rate, but a store with
  // configured delivery options has an FE delivery step that pushes its own
  // `Shipping` item on every change — leaving a shipping method in place would
  // charge freight twice. There it goes straight into the items instead.
  const hasConfiguredMethods = Boolean(opts.shippingProviderConfig?.defaultOptions?.methods?.length)

  const { error: createError, data: createData } = await qliroApi.checkout.createOrder(
    hasConfiguredMethods
      ? {
          ...qliroPayload,
          OrderItems: [
            ...qliroPayload.OrderItems,
            {
              MerchantReference: shippingInfo.id,
              Description: shippingInfo.name,
              Type: 'Shipping',
              Quantity: 1,
              PricePerItemIncVat: shippingPriceIncVat,
              PricePerItemExVat: shippingPriceExVat,
              VatRate: convertMinorUnitToValue(shippingInfo.tax_rate)
            }
          ]
        }
      : {
          ...qliroPayload,
          AvailableShippingMethods: [
            {
              MerchantReference: shippingInfo.id,
              DisplayName: shippingInfo.name,
              PriceIncVat: shippingPriceIncVat,
              PriceExVat: shippingPriceExVat
            }
          ]
        }
  )

  if (createError || !createData) {
    res.status(400).json({ error: createError || 'Unknown error' })
    return
  }

  // Qliro createOrder returns only OrderId, need retrieveOrder for HTML snippet
  const { error: retrieveError, data: qliroOrder } = await qliroApi.checkout.retrieveOrder(
    createData.OrderId
  )

  if (retrieveError || !qliroOrder) {
    res.status(400).json({ error: retrieveError || 'Unknown error' })
    return
  }

  res.status(200).json({ storefrontOrderResponse: backendOrder, qliroResponse: qliroOrder })
}

const postnordShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const {
    storeId,
    items,
    cartDiscountCode,
    cartStateUuid,
    countryCode: requestedCountryCode
  }: CreateOrderRequest = JSON.parse(req.body)
  const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
  const locale = opts.locale || localeForCredentials
  const countryCode = resolveQliroCountry(opts, requestedCountryCode)

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'QLIRO'
    },
    shipping: {
      provider: 'POSTNORD'
    }
  })

  const qliroPayload = generateCreateQliroOrderPayload({
    opts,
    backendOrder,
    items,
    cartDiscountCode,
    cartStateUuid,
    locale,
    countryCode
  })

  const { error: createError, data: createData } = await qliroApi.checkout.createOrder(qliroPayload)

  if (createError || !createData) {
    res.status(400).json({ error: createError || 'Unknown error' })
    return
  }

  const { error: retrieveError, data: qliroOrder } = await qliroApi.checkout.retrieveOrder(
    createData.OrderId
  )

  if (retrieveError || !qliroOrder) {
    res.status(400).json({ error: retrieveError || 'Unknown error' })
    return
  }

  res.status(200).json({
    storefrontOrderResponse: backendOrder,
    qliroResponse: qliroOrder
  })
}

const ingridShipping = (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  void ingridShippingProvider(req, res, opts, async (ingridResponse) => {
    const {
      storeId,
      items,
      cartDiscountCode,
      cartStateUuid,
      countryCode: requestedCountryCode
    }: CreateOrderRequest = JSON.parse(req.body)
    const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
    const locale = opts.locale || localeForCredentials
    const countryCode = resolveQliroCountry(opts, requestedCountryCode)

    const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
    const backendOrder = await storefrontApi.orders.createStorefrontOrder({
      storeId,
      backendOrderLines,
      cartDiscountCode,
      customerId: opts.customerId,
      paymentProviderInfo: {
        providerName: 'QLIRO'
      },
      shipping: {
        provider: 'INGRID',
        providerSessionId: ingridResponse.session.checkout_session_id
      }
    })

    const qliroPayload = generateCreateQliroOrderPayload({
      opts,
      backendOrder,
      items,
      cartDiscountCode,
      cartStateUuid,
      locale,
      countryCode
    })

    const { error: createError, data: createData } =
      await qliroApi.checkout.createOrder(qliroPayload)

    if (createError || !createData) {
      res.status(400).json({ error: createError || 'Unknown error' })
      return
    }

    const { error: retrieveError, data: qliroOrder } = await qliroApi.checkout.retrieveOrder(
      createData.OrderId
    )

    if (retrieveError || !qliroOrder) {
      res.status(400).json({ error: retrieveError || 'Unknown error' })
      return
    }

    res.status(200).json({
      storefrontOrderResponse: backendOrder,
      qliroResponse: qliroOrder,
      ingridResponse
    })
  })
}

const nshiftShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const nshiftResponse = await nshiftShippingProvider(req, opts)

  const {
    storeId,
    items,
    cartDiscountCode,
    cartStateUuid,
    countryCode: requestedCountryCode
  }: CreateOrderRequest = JSON.parse(req.body)
  const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
  const locale = opts.locale || localeForCredentials
  const countryCode = resolveQliroCountry(opts, requestedCountryCode)

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'QLIRO'
    },
    shipping: {
      provider: 'NSHIFT',
      providerSessionId: nshiftResponse.sessionId
    }
  })

  const qliroPayload = generateCreateQliroOrderPayload({
    opts,
    backendOrder,
    items,
    cartDiscountCode,
    cartStateUuid,
    locale,
    countryCode
  })

  const { error: createError, data: createData } = await qliroApi.checkout.createOrder(qliroPayload)

  if (createError || !createData) {
    res.status(400).json({ error: createError || 'Unknown error' })
    return
  }

  const { error: retrieveError, data: qliroOrder } = await qliroApi.checkout.retrieveOrder(
    createData.OrderId
  )

  if (retrieveError || !qliroOrder) {
    res.status(400).json({ error: retrieveError || 'Unknown error' })
    return
  }

  res.status(200).json({
    storefrontOrderResponse: backendOrder,
    qliroResponse: qliroOrder,
    nshiftResponse
  })
}

export const qliroPaymentProvider = async (
  req: NextApiRequest,
  res: NextApiResponse,
  opts: CreateOrderHandlerOpts
) => {
  const { shippingProviderConfig } = opts

  switch (shippingProviderConfig?.provider ?? 'DEFAULT') {
    case 'INGRID':
      return ingridShipping(req, res, opts)
    case 'POSTNORD':
      return postnordShipping(req, res, opts)
    case 'NSHIFT':
      return nshiftShipping(req, res, opts)
    case 'DEFAULT':
      return simpleShipping(req, res, opts)
  }
}
